import { FormEvent, useEffect, useMemo, useState } from 'react'
import { UserPlus, Shield, GraduationCap, Trash2, X, Check, ArrowUpDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  addMember,
  assignClassTeacher,
  fetchClasses,
  fetchMembers,
  removeMember,
  unassignClassTeacher,
  updateMemberRole,
} from '../api/client'
import type { ClassSummary, OrgMember } from '../api/types'
import { useOrgContext } from '../state/OrgContext'

export default function MembersPage() {
  const { t } = useTranslation('pages')
  const { activeSubId, activeRole } = useOrgContext()

  const [members, setMembers] = useState<OrgMember[]>([])
  const [classes, setClasses] = useState<ClassSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [savingMember, setSavingMember] = useState(false)
  const [removingUserId, setRemovingUserId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')

  const [assignModal, setAssignModal] = useState<number | null>(null)
  const [assignedClassIds, setAssignedClassIds] = useState<Set<number>>(new Set())
  const [savingAssignments, setSavingAssignments] = useState(false)
  const [assignError, setAssignError] = useState<string | null>(null)
  const [togglingRoleUserId, setTogglingRoleUserId] = useState<number | null>(null)

  const canManage = activeRole === 'ORG_MANAGER' || activeRole === 'ADMIN'

  const loadData = async (orgId: number) => {
    setLoading(true)
    setError(null)
    try {
      const [memberResp, classList] = await Promise.all([fetchMembers(orgId), fetchClasses()])
      setMembers(memberResp.members)
      setClasses(classList)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('members.loadError'))
      setMembers([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (activeSubId) {
      loadData(activeSubId)
    }
  }, [activeSubId])

  const onAddMember = async (event: FormEvent) => {
    event.preventDefault()
    if (!activeSubId) {
      setError(t('members.selectOrgFirst'))
      return
    }
    const trimmedName = fullName.trim()
    const trimmedEmail = email.trim()
    if (!trimmedName || !trimmedEmail) {
      setError(t('members.nameEmailRequired'))
      return
    }

    setSavingMember(true)
    setError(null)
    try {
      const created = await addMember(activeSubId, { full_name: trimmedName, email: trimmedEmail, role: 'TEACHER' })
      setMembers((prev) => {
        const existing = prev.filter((m) => m.id !== created.id)
        return [created, ...existing]
      })
      setFullName('')
      setEmail('')
    } catch (err) {
      setError(err instanceof Error ? err.message : t('members.addError'))
    } finally {
      setSavingMember(false)
    }
  }

  const onRemoveMember = async (member: OrgMember) => {
    if (!activeSubId) return
    setRemovingUserId(member.id)
    setError(null)
    try {
      await removeMember(activeSubId, member.id)
      setMembers((prev) => prev.filter((m) => m.id !== member.id))
    } catch (err) {
      setError(err instanceof Error ? err.message : t('members.removeError'))
    } finally {
      setRemovingUserId(null)
    }
  }

  const openAssignDialog = (member: OrgMember) => {
    setAssignError(null)
    setAssignModal(member.id)
    setAssignedClassIds(new Set(member.assigned_class_ids || []))
  }

  const toggleClass = (classId: number) => {
    setAssignedClassIds((prev) => {
      const next = new Set(prev)
      if (next.has(classId)) {
        next.delete(classId)
      } else {
        next.add(classId)
      }
      return next
    })
  }

  const saveAssignments = async () => {
    const member = members.find((m) => m.id === assignModal)
    if (!member || !activeSubId) return

    const initial = member.assigned_class_ids || []
    const toAdd = [...assignedClassIds].filter((id) => !initial.includes(id))
    const toRemove = initial.filter((id) => !assignedClassIds.has(id))

    setSavingAssignments(true)
    setAssignError(null)
    try {
      await Promise.all([
        ...toAdd.map((classId) => assignClassTeacher(classId, member.id)),
        ...toRemove.map((classId) => unassignClassTeacher(classId, member.id)),
      ])
      await loadData(activeSubId)
      setAssignModal(null)
      setAssignedClassIds(new Set())
    } catch (err) {
      setAssignError(err instanceof Error ? err.message : t('members.assignError'))
    } finally {
      setSavingAssignments(false)
    }
  }

  const onToggleRole = async (member: OrgMember) => {
    if (!activeSubId) return
    const newRole = member.role === 'ORG_MANAGER' ? 'TEACHER' : 'ORG_MANAGER'
    setTogglingRoleUserId(member.id)
    setError(null)
    try {
      const updated = await updateMemberRole(activeSubId, member.id, newRole)
      setMembers((prev) => prev.map((m) => (m.id === updated.id ? updated : m)))
    } catch (err) {
      setError(err instanceof Error ? err.message : t('members.roleError'))
    } finally {
      setTogglingRoleUserId(null)
    }
  }

  const roleBadge = (role: string) => {
    switch (role) {
      case 'ORG_MANAGER':
        return (
          <span className="badge badge-copper">
            <Shield size={12} />
            {t('common.roles.manager')}
          </span>
        )
      case 'TEACHER':
        return (
          <span className="badge badge-info">
            <GraduationCap size={12} />
            {t('common.roles.teacher')}
          </span>
        )
      default:
        return <span className="badge badge-neutral">{role}</span>
    }
  }

  if (!activeSubId) {
    return (
      <div className="animate-fade-in">
        <div className="page-header">
          <h2 className="page-header-title">{t('members.title')}</h2>
          <p className="page-header-sub">{t('members.noSubtitle')}</p>
        </div>
      </div>
    )
  }

  if (!canManage) {
    return (
      <div className="animate-fade-in">
        <div className="page-header">
          <h2 className="page-header-title">{t('members.title')}</h2>
          <p className="page-header-sub">{t('members.accessDeniedSubtitle')}</p>
        </div>
      </div>
    )
  }

  const assigningMember = members.find((m) => m.id === assignModal) ?? null

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <h2 className="page-header-title">{t('members.title')}</h2>
        <p className="page-header-sub">{t('members.manageSubtitle')}</p>
      </div>

      {error && (
        <div className="card card-padding mb-20" style={{ borderLeft: '3px solid var(--color-danger)' }}>
          <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>
        </div>
      )}

      <div style={{ maxWidth: 800 }}>
        <div className="card card-padding mb-24">
          <h3 className="card-title mb-16">{t('members.addCard')}</h3>
          <form className="flex gap-12" onSubmit={onAddMember}>
            <div className="form-group" style={{ flex: 1 }}>
              <input
                type="text"
                className="form-input"
                placeholder={t('members.fullNamePlaceholder')}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <input
                type="email"
                className="form-input"
                placeholder={t('members.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <button className="btn btn-primary" type="submit" disabled={savingMember}>
              <UserPlus size={16} />
              {savingMember ? t('common.adding') : t('common.add')}
            </button>
          </form>
        </div>

        <div className="card">
          {loading ? (
            <div className="card-padding">
              <p className="text-sm text-muted">{t('members.membersLoading')}</p>
            </div>
          ) : members.length === 0 ? (
            <div className="card-padding">
              <p className="text-sm text-muted">{t('members.membersEmpty')}</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('members.colName')}</th>
                    <th>{t('members.colEmail')}</th>
                    <th>{t('members.colRole')}</th>
                    <th>{t('members.colClasses')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id}>
                      <td className="font-semibold">{m.full_name || '-'}</td>
                      <td className="text-muted">{m.email}</td>
                      <td>{roleBadge(m.role)}</td>
                      <td>
                        <span className="chip">
                          <GraduationCap size={14} />
                          {t('members.nClasses', { count: m.assigned_class_count || 0 })}
                        </span>
                      </td>
                      <td>
                        <div className="flex gap-8">
                          <button
                            className="btn btn-outline btn-xs"
                            onClick={() => openAssignDialog(m)}
                          >
                            {t('members.assignClasses')}
                          </button>
                          {/* Hide demote button if member is org owner and current user is not admin */}
                          <button
                            className="btn btn-outline btn-xs"
                            onClick={() => onToggleRole(m)}
                            disabled={togglingRoleUserId === m.id || (m.role === 'ORG_MANAGER' && !!m.is_owner && activeRole !== 'ADMIN')}
                            title={m.role === 'ORG_MANAGER' && m.is_owner && activeRole !== 'ADMIN' ? t('members.cantDemoteOwner') : m.role === 'ORG_MANAGER' ? t('members.makeTeacher') : t('members.makeManager')}
                          >
                            <ArrowUpDown size={12} />
                            {togglingRoleUserId === m.id
                              ? t('common.updating')
                              : m.role === 'ORG_MANAGER'
                                ? t('members.makeTeacher')
                                : t('members.makeManager')}
                          </button>
                          {m.role === 'TEACHER' && (
                            <button
                              className="btn btn-danger btn-xs"
                              onClick={() => onRemoveMember(m)}
                              disabled={removingUserId === m.id}
                            >
                              <Trash2 size={12} />
                              {removingUserId === m.id ? t('common.removing') : t('common.remove')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Assign classes modal */}
      {assignModal !== null && (
        <div className="modal-overlay" onClick={() => setAssignModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">
                {t('members.modalTitle', { name: assigningMember?.full_name || assigningMember?.email })}
              </span>
              <button className="btn btn-icon btn-ghost" onClick={() => setAssignModal(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              {classes.length === 0 ? (
                <p className="text-sm text-muted">{t('members.noClassesAvailable')}</p>
              ) : (
                classes.map((c) => (
                  <div
                    key={c.id}
                    className="checkbox-item"
                    onClick={() => toggleClass(c.id)}
                  >
                    <div className={`checkbox-box ${assignedClassIds.has(c.id) ? 'checked' : ''}`}>
                      {assignedClassIds.has(c.id) && <Check size={14} />}
                    </div>
                    <div>
                      <span className="checkbox-label">{c.name}</span>
                    </div>
                  </div>
                ))
              )}
              {assignError && (
                <p className="text-sm mt-12" style={{ color: 'var(--color-danger)' }}>{assignError}</p>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline btn-sm" onClick={() => setAssignModal(null)}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-primary btn-sm" onClick={saveAssignments} disabled={savingAssignments}>
                {savingAssignments ? t('common.saving') : t('members.saveAssignments')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
